<template>
  <div class="wizard-body columns content-box small-9">
    <empty-state
      :image-src="finishSuccessImage"
      :title="$t('OPPORTUNITY_PIPELINES.CREATE_FLOW.FINISH.TITLE')"
      :message="$t('OPPORTUNITY_PIPELINES.CREATE_FLOW.FINISH.MESSAGE')"
    >
      <div class="medium-12 columns text-center">
        <woot-button class="success" :is-loading="isSubmitting" @click="submit">
          {{ submitButtonLabel }}
        </woot-button>
      </div>
    </empty-state>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import EmptyState from '../../../../../components/widgets/EmptyState';
import { clearDraft, patchDraft, readDraft } from './draftState';
import finishSuccessImage from '../../../../../assets/images/opportunity-pipelines/finish-success.svg';
import { sortStagesByPosition } from '../../../../../helper/opportunityPipelineHelper';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

export default {
  components: {
    EmptyState,
  },
  mixins: [alertMixin],
  data() {
    return {
      finishSuccessImage,
      isSubmitting: false,
    };
  },
  computed: {
    isNewDraftFlow() {
      return this.$route.name === 'settings_opportunity_pipelines_new_finish';
    },
    submitButtonLabel() {
      const key = this.isNewDraftFlow
        ? 'OPPORTUNITY_PIPELINES.CREATE_FLOW.FINISH.BUTTON'
        : 'OPPORTUNITY_PIPELINES.CREATE_FLOW.FINISH.BUTTON_EDIT';
      return this.$t(key);
    },
  },
  methods: {
    async submit() {
      if (!this.isNewDraftFlow) {
        this.$router.replace({ name: 'settings_opportunity_pipelines_list' });
        return;
      }

      const draft = readDraft();
      if (!draft.basicInfo?.name) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.CREATE_PIPELINE'));
        this.$router.replace({ name: 'settings_opportunity_pipelines_new' });
        return;
      }

      this.isSubmitting = true;
      // Declared outside the try block (not just destructured from `draft`
      // inside it) so the catch block below can tell whether the pipeline
      // itself was created before deciding where to send the user back to.
      let pipelineId = draft.pipelineId;
      try {
        // If a previous attempt created the pipeline but failed on a later
        // step (users/inboxes/stages/loss reasons), the draft still holds its
        // id here. Reusing it instead of calling create() again avoids a
        // "name already taken" error that would otherwise permanently block
        // this draft from ever finishing.
        if (!pipelineId) {
          const pipeline = await this.$store.dispatch(
            'opportunityPipelines/create',
            { pipeline: draft.basicInfo }
          );
          pipelineId = pipeline.id;
          patchDraft({ pipelineId });
        }

        if (draft.users?.length) {
          await this.$store.dispatch('opportunityPipelines/saveUsers', {
            pipelineId,
            userIds: draft.users,
          });
        }

        if (draft.inboxes?.length) {
          await this.$store.dispatch('opportunityPipelines/saveInboxes', {
            pipelineId,
            inboxIds: draft.inboxes,
          });
        }

        const stages = sortStagesByPosition(draft.stages || []).map(
          (stage, index) => ({
            name: stage.name,
            description: stage.description,
            color: stage.color,
            sla_days: Number(stage.sla_days || 0),
            status: stage.status || 'active',
            position: index + 1,
          })
        );

        if (stages.length) {
          const existingStagesData = await this.$store.dispatch(
            'opportunityPipelines/getStages',
            pipelineId
          );
          const existingStages = sortStagesByPosition(existingStagesData || []);
          const excessStages = existingStages.slice(stages.length);

          // When the draft has fewer stages than the defaults, remove the
          // excess first. This prevents uniqueness collisions with names that
          // should no longer exist (for example, an old "Ganho" stage).
          await excessStages.reduce((chain, stage) => {
            return chain.then(() =>
              this.$store.dispatch('opportunityPipelines/removeStage', {
                pipelineId,
                stageId: stage.id,
              })
            );
          }, Promise.resolve());

          const existingStagesToSync = existingStages.slice(
            0,
            Math.min(existingStages.length, stages.length)
          );

          // Prevent transient uniqueness collisions when swapping/renaming
          // stage names (e.g. A->B while B still exists).
          await existingStagesToSync.reduce((chain, existingStage, index) => {
            return chain.then(() => {
              const temporaryName = `__tmp_stage_${existingStage.id}_${index}`;
              return this.$store.dispatch('opportunityPipelines/updateStage', {
                pipelineId,
                stageId: existingStage.id,
                payload: { name: temporaryName },
              });
            });
          }, Promise.resolve());

          // Avoid deleting the default stages because the backend enforces a
          // minimum stage count. Instead, sync by updating existing rows and
          // creating only the missing ones.
          await stages.reduce((chain, stage, index) => {
            return chain.then(async () => {
              const existingStage = existingStages[index];
              const stagePayload = {
                name: stage.name,
                description: stage.description,
                color: stage.color,
                sla_days: stage.sla_days,
                position: stage.position,
              };
              const targetStatus = stage.status || 'active';

              if (existingStage) {
                await this.$store.dispatch('opportunityPipelines/updateStage', {
                  pipelineId,
                  stageId: existingStage.id,
                  payload: stagePayload,
                });

                const existingStatus = existingStage.status || 'active';
                if (existingStatus !== targetStatus) {
                  await this.$store.dispatch(
                    'opportunityPipelines/updateStageStatus',
                    {
                      pipelineId,
                      stageId: existingStage.id,
                      status: targetStatus,
                    }
                  );
                }
                return;
              }

              const createdStage = await this.$store.dispatch(
                'opportunityPipelines/createStage',
                { pipelineId, payload: stagePayload }
              );
              const createdStageId = createdStage?.id;

              if (createdStageId && targetStatus !== 'active') {
                await this.$store.dispatch(
                  'opportunityPipelines/updateStageStatus',
                  {
                    pipelineId,
                    stageId: createdStageId,
                    status: targetStatus,
                  }
                );
              }
            });
          }, Promise.resolve());
        }

        const lossReasons = (draft.lossReasons || []).map(reason => ({
          name: reason.name,
          description: reason.description,
          status: reason.status || 'active',
        }));

        if (lossReasons.length) {
          const existingReasonsData = await this.$store.dispatch(
            'opportunityPipelines/getLossReasons',
            pipelineId
          );
          const existingNames = new Set(
            (existingReasonsData || []).map(reason => reason.name.toLowerCase())
          );

          // Skip reasons a previous, partially-failed attempt already
          // created, so retrying this step doesn't hit a uniqueness error.
          const missingReasons = lossReasons.filter(
            reason => !existingNames.has(reason.name.toLowerCase())
          );

          await Promise.all(
            missingReasons.map(reason =>
              this.$store.dispatch('opportunityPipelines/createLossReason', {
                pipelineId,
                payload: reason,
              })
            )
          );
        }

        clearDraft();
        this.$router.replace({ name: 'settings_opportunity_pipelines_list' });
      } catch (error) {
        // After several steps (users/inboxes/stages/loss reasons), a
        // generic message here left the user unsure whether the whole
        // wizard failed or just, say, the pipeline name being a duplicate -
        // surface the backend's actual validation message when there is
        // one (e.g. "Name has already been taken") instead of always the
        // same fallback string.
        const parsedError = parseAPIErrorResponse(error);
        const errorMessage =
          (typeof parsedError === 'string' && parsedError) ||
          this.$t('OPPORTUNITY_PIPELINES.ERRORS.CREATE_PIPELINE');
        this.showAlert(errorMessage);

        // A duplicate/invalid name can only really be fixed by going back
        // to step 1 - send the user there instead of leaving them stuck on
        // this screen with no obvious next action. If the pipeline itself
        // was already created and a later step failed, stay here instead -
        // going back to step 1 would suggest the name is the problem when
        // it isn't.
        if (!pipelineId) {
          this.$router.replace({ name: 'settings_opportunity_pipelines_new' });
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
